import { Component, OnInit } from '@angular/core';
import { TrpcService } from '../../services/trpc/trpc.service';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ActivatedRoute } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { environment } from '../../../environments/environment';
import { AccessCheckResult } from '../../../../../express-tRPC/src/invoice-library-shared/cross-domain-types';

const COMPONENT_FEATURE_NAME = 'Portal Developer';

@Component({
  selector: 'app-lot-settings',
  templateUrl: './lot-settings.component.html',
  styleUrls: ['./lot-settings.component.scss'],
  providers: [ConfirmationService]
})
export class LotSettingsComponent implements OnInit {

  version = environment.version;
  activeFile = environment.activeFile;

  // Store the location ID for access within the template
  locationId = '';

  // Access control
  hasAccess: boolean = false;
  checkingAccess: boolean = true;

  // LPR Settings
  lprEnabled: boolean = false;
  loadingLprStatus: boolean = false;
  updatingLprStatus: boolean = false;

  // LPR Activation
  activatingLpr: boolean = false;
  activationMessages: string[] = [];
  showActivationOutput: boolean = false;

  constructor(
    public route: ActivatedRoute,
    private trpcService: TrpcService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private titleService: Title
  ) {}

  async ngOnInit() {
    this.trpcService.currentLocationObservable.subscribe((location: string) => {
      this.locationId = location;
      this.titleService.setTitle(`Lot Settings | ${location}`);
    });

    await this.checkAccessToFeature();

    if (this.hasAccess) {
      await this.loadLprStatus();
    }
  }

  /**
   * Check if the current user has access to this feature
   */
  async checkAccessToFeature() {
    this.checkingAccess = true;

    try {
      const response: AccessCheckResult = await this.trpcService.trpc.lots.checkAccessToFeature.mutate({
        feature: COMPONENT_FEATURE_NAME
      });

      this.hasAccess = response.result === 'allowed';

      if (!this.hasAccess) {
        this.messageService.add({
          severity: 'error',
          detail: 'You do not have access to this feature. Portal Developer role required.',
          sticky: true
        });
      }

    } catch (error: any) {
      this.hasAccess = false;
      this.messageService.add({
        severity: 'error',
        detail: 'Unable to check access to feature.',
        sticky: true
      });
    } finally {
      this.checkingAccess = false;
    }
  }

  /**
   * Load the current LPR feature status
   */
  async loadLprStatus() {
    this.loadingLprStatus = true;

    try {
      const response = await this.trpcService.trpc.settings.getLprStatus.query();
      this.lprEnabled = response.enabled;
      this.locationId = response.locationId;
    } catch (error: any) {
      this.messageService.add({
        severity: 'error',
        detail: error.message || 'Failed to load LPR status',
      });
    } finally {
      this.loadingLprStatus = false;
    }
  }

  /**
   * Handle LPR toggle change
   */
  onLprToggleChange(event: any) {
    const newValue = event.checked;

    // If disabling, show confirmation dialog
    if (!newValue) {
      this.confirmationService.confirm({
        message: 'Disabling LPR is not backwards compatible and should only be used for troubleshooting. Are you sure you want to continue?',
        header: 'Confirm Disable',
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.updateLprStatus(newValue);
        },
        reject: () => {
          // Revert the toggle
          this.lprEnabled = !newValue;
          this.messageService.add({
            severity: 'info',
            summary: 'Cancelled',
            detail: 'LPR status was not changed.'
          });
        }
      });
    } else {
      // If enabling, update immediately
      this.updateLprStatus(newValue);
    }
  }

  /**
   * Update the LPR feature status
   */
  async updateLprStatus(enabled: boolean) {
    this.updatingLprStatus = true;

    try {
      await this.trpcService.trpc.settings.setLprStatus.mutate({ enabled });

      this.messageService.add({
        severity: 'success',
        detail: `LPR feature ${enabled ? 'enabled' : 'disabled'} successfully.`,
      });

      // Reload status to confirm
      await this.loadLprStatus();

    } catch (error: any) {
      this.messageService.add({
        severity: 'error',
        detail: error.message || 'Failed to update LPR status',
      });

      // Reload to revert UI to actual state
      await this.loadLprStatus();

    } finally {
      this.updatingLprStatus = false;
    }
  }

  /**
   * Activate LPR for the current location
   */
  async activateLpr() {
    this.activatingLpr = true;
    this.activationMessages = [];
    this.showActivationOutput = true;

    try {
      const response = await this.trpcService.trpc.settings.activateLpr.mutate();

      this.activationMessages = response.messages;

      if (response.success) {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'LPR activation completed successfully!',
        });

        // Reload LPR status
        await this.loadLprStatus();
      } else {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: response.error || 'LPR activation failed',
        });
      }

    } catch (error: any) {
      this.activationMessages = [`❌ Failed to activate LPR: ${error.message || 'Unknown error'}`];
      this.messageService.add({
        severity: 'error',
        detail: error.message || 'Failed to activate LPR',
      });
    } finally {
      this.activatingLpr = false;
    }
  }

  /**
   * Clear activation output
   */
  clearActivationOutput() {
    this.activationMessages = [];
    this.showActivationOutput = false;
  }

  backToPortal(): void {
    location.href = environment.SITE_URL_LOCAL;
  }
}
